import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.jsx';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
function TestimonialsPage() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [headerRef, headerVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [mainCardRef, mainCardVisible] = useIntersectionObserver({
    threshold: 0.1
  });
  const [gridRef, gridVisible] = useIntersectionObserver({
    threshold: 0.1
  });

  // Handle testimonial transitions with animation
  const handleTestimonialChange = index => {
    if (index !== activeTestimonial) {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveTestimonial(index);
        setIsTransitioning(false);
      }, 300);
    }
  };
  const testimonials = [{
    id: 1,
    name: 'Sarah M.',
    role: 'Spiritual Director, Coaching Client',
    disclaimer: 'Name changed to preserve confidentiality',
    content: 'I would sometimes "please" certain clients, over-functioning to keep them comfortable. In our work, I practiced allowing sensations that activated my pleasing parts and allowed energy to move through me. Now, when those sensations arise, I can self-soothe, down-regulate, and stay in Self more of the time.',
    rating: 5,
    highlight: 'Transformative work'
  }, {
    id: 2,
    name: 'Jennifer L.',
    role: 'Teacher & Mother of Two, Coaching Client',
    disclaimer: 'Name changed to preserve confidentiality',
    content: 'I was used to feeling invisible in my own life. Yasi helped me understand how my internalized patriarchy and unsafety I felt was driving me to people-please. The work we did together gave me the tools to feel and trust my feelings, reduce the shame I felt, find and know my voice. I am building a beautiful life where I feel seen by myself and my loved ones.',
    rating: 5,
    highlight: 'Finally feeling seen'
  }, {
    id: 3,
    name: 'Amanda K.',
    role: 'Entrepreneur, Coaching Client',
    disclaimer: 'Name changed to preserve confidentiality',
    content: 'I learned how to say no with confidence and stopped over-explaining myself. Yasi created such a safe space for me to explore these patterns. I\'m now running my business on my terms and not other people\'s.',
    rating: 5,
    highlight: 'Empowering & practical'
  }, {
    id: 4,
    name: 'Rachel T.',
    role: 'Healthcare Worker, Coaching Client',
    disclaimer: 'Name changed to preserve confidentiality',
    content: 'I was constantly putting others first, professionally, and with friends, family, and at church and I was experiencing cycles of burnout. I would feel okay with self-care and healthy boundaries for a while, only to reach burnout again. Working with Yasi helped me understand the systemic roots of my people-pleasing, which helped me release the shame I\'d been carrying all along. I had been hiding parts of me from peers, supervisors, and my therapist because of the shame. Yasi helped me navigate my way back into connection with those who genuinely are for me and have wanted to support my growth.',
    rating: 5,
    highlight: 'Compassionate & insightful'
  }];
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div data-name="Testimonials Page">
      <Navigation variant="white" />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-sage-50 to-white" data-name="Page Hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            Client Stories
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Hear from women who've transformed their relationship with people-pleasing and stepped into authentic, empowered living.
          </p>
        </div>
      </section>

      {/* Main Testimonials Section */}
      <section className="py-20 bg-white" data-name="Featured Testimonial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Testimonial Display with Glass Effect */}
          <div ref={mainCardRef} className={`max-w-4xl mx-auto mb-12 transition-all duration-800 ${mainCardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className={`glass-light rounded-3xl shadow-2xl p-8 md:p-12 relative border-2 border-white/30 transition-all duration-300 ${isTransitioning ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
              {/* Quote Icon with Animation */}
              <svg className="w-16 h-16 text-warm-200 absolute top-8 left-8 opacity-50 animate-float" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>

              {/* Stars with Stagger Animation */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => <svg key={i} className="w-6 h-6 text-warm-400 hover:scale-110 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20" style={{
                animation: `fadeInUp 0.4s ease-out ${i * 0.1}s both`
              }}>
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>)}
              </div>

              {/* Content */}
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8 relative z-10">
                "{testimonials[activeTestimonial].content}"
              </p>

              {/* Author with Hover Effect */}
              <div className="flex items-center gap-4 group">
                <div className="w-14 h-14 rounded-full bg-sage-200/70 flex items-center justify-center text-sage-700 text-xl font-semibold group-hover:scale-110 transition-transform duration-300 shadow-md">
                  {testimonials[activeTestimonial].name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-sage-800 text-lg">
                    {testimonials[activeTestimonial].name}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {testimonials[activeTestimonial].role}
                  </p>
                  <p className="text-gray-500 text-xs italic mt-1">
                    {testimonials[activeTestimonial].disclaimer}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Dots with Enhanced Interaction */}
          <div className="flex justify-center gap-3 mb-12">
            {testimonials.map((_, index) => <button key={index} onClick={() => handleTestimonialChange(index)} className={`transition-all duration-300 rounded-full hover:scale-110 ${index === activeTestimonial ? 'w-12 h-3 bg-warm-500 shadow-lg' : 'w-3 h-3 bg-sage-300 hover:bg-sage-400'}`} aria-label={`View testimonial ${index + 1}`} data-tracking-id={`btn-3882952363ba92af_${index}`} />)}
          </div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-20 bg-sage-50" data-name="All Testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-800 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-6">
              All Client Stories
            </h2>
            <p className="text-lg text-gray-600">
              Every journey is unique. Read more stories from women who've found their voice and reclaimed their power.
            </p>
          </div>

          {/* Testimonial Grid - All Cards */}
          <div ref={gridRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => <div key={testimonial.id} className={`glass-light rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-2 hover:scale-105 border border-white/20 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${index === activeTestimonial ? 'ring-2 ring-warm-400' : ''}`} style={{
            transitionDelay: `${index * 100}ms`
          }} onClick={() => handleTestimonialChange(index)}>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => <svg key={i} className="w-4 h-4 text-warm-400 hover:scale-125 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>)}
                </div>
                <p className="text-gray-700 text-sm mb-4">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-3 mt-4">
                  <div className="w-10 h-10 rounded-full bg-sage-200/70 flex items-center justify-center text-sage-700 text-sm font-semibold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sage-700 text-sm">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {testimonial.role}
                    </p>
                    <p className="text-gray-500 text-xs italic mt-1">
                      {testimonial.disclaimer}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-white to-sage-50" data-name="Call to Action">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Join the women who've transformed their lives. Let's work together to help you break free from people-pleasing.
          </p>
          <Link to="/contact" className="inline-block bg-warm-500 text-white px-8 py-4 rounded-full font-medium hover:bg-warm-600 transition-all duration-300 hover:shadow-lg hover:scale-105" data-tracking-id="link-e22143691847a288">
            Book Your Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>;
}
export default TestimonialsPage;